/**
 * Heading Extractor Utility
 *
 * Extracts and filters heading blocks from BlockNote documents.
 * Builds hierarchical structure for Table of Contents rendering.
 *
 * Features:
 * - Extracts all heading blocks from document
 * - Filters by heading levels (H1-H6)
 * - Filters out hidden headings
 * - Builds nested hierarchy structure
 * - Handles empty headings gracefully
 * - Optimized for large documents (100+ headings)
 * - Handles deeply nested structures (H1→H6 jumps)
 * - Truncates very long heading text
 *
 * Usage:
 *   const headings = extractHeadings(editor.document, {
 *     includedLevels: [2, 3],
 *     hiddenHeadings: ['intro-2']
 *   });
 */

/**
 * Maximum heading text length before truncation
 * Helps with performance and display
 */
const MAX_HEADING_LENGTH = 200;

/**
 * Recursively extracts text from inline content
 *
 * BlockNote inline content can be nested (e.g., links contain text content).
 * This function handles all inline content types including:
 * - Plain text: { type: "text", text: "..." }
 * - Links: { type: "link", content: [...], href: "..." }
 * - Styled text: { type: "text", text: "...", styles: {...} }
 *
 * @param {Object|Array} content - Inline content item or array of items
 * @returns {string} Extracted text
 */
function extractTextFromContent(content) {
  if (!content) return '';

  // If it's an array, recursively extract from each item
  if (Array.isArray(content)) {
    return content.map(item => extractTextFromContent(item)).join('');
  }

  // If it's a simple text node
  if (content.text) {
    return content.text;
  }

  // If it's a link or other container with nested content
  if (content.content) {
    return extractTextFromContent(content.content);
  }

  return '';
}

/**
 * Extracts heading blocks from a document
 *
 * Traverses the document and returns all heading blocks with their
 * text content, level, and ID. Skips empty headings and handles
 * edge cases like missing content or malformed blocks.
 *
 * @param {Array} document - BlockNote document (array of blocks)
 * @returns {Array} Array of heading objects
 *
 * @example
 *   extractAllHeadings(document)
 *   // Returns: [
 *   //   { id: "intro", text: "Introduction", level: 2 },
 *   //   { id: "features", text: "Features", level: 2 }
 *   // ]
 */
export function extractAllHeadings(document) {
  // Guard against invalid input
  if (!Array.isArray(document)) {
    return [];
  }

  const headings = [];

  // Performance optimization: Use for loop instead of forEach for large documents
  for (let i = 0; i < document.length; i++) {
    const block = document[i];

    // Skip non-heading blocks
    if (!block || block.type !== 'heading') {
      continue;
    }

    // Extract text content from the block
    // Handle various content formats including links, styled text, etc.
    let text = '';

    try {
      if (Array.isArray(block.content) && block.content.length > 0) {
        // Use recursive extraction to handle nested content (links, etc.)
        text = extractTextFromContent(block.content).trim();
      }
    } catch (error) {
      continue;
    }

    // Skip empty headings (no text content)
    if (!text) {
      continue;
    }

    // Truncate very long headings for performance
    if (text.length > MAX_HEADING_LENGTH) {
      text = text.substring(0, MAX_HEADING_LENGTH) + '...';
    }

    // Get heading level with fallback
    const level = block.props?.level || 1;

    // Validate level is in valid range
    if (level < 1 || level > 6) {
      continue;
    }

    // Prefer the block's unique id for DOM targeting; also keep prop id (slug)
    const blockId = block.id || `block-${i}`;
    const id = block.props?.id || blockId || `heading-${i}`;

    headings.push({
      id: id,
      text: text,
      level: level,
      blockId: blockId,
    });
  }

  return headings;
}

/**
 * Filters headings by included levels
 *
 * Takes a list of headings and returns only those whose level
 * is in the includedLevels array.
 *
 * @param {Array} headings - Array of heading objects
 * @param {Array} includedLevels - Array of levels to include (1-6)
 * @returns {Array} Filtered array of heading objects
 *
 * @example
 *   filterByLevels(headings, [2, 3])
 *   // Returns only H2 and H3 headings
 */
export function filterByLevels(headings, includedLevels) {
  // Guard against invalid input
  if (!Array.isArray(headings)) {
    return [];
  }

  if (!Array.isArray(includedLevels) || includedLevels.length === 0) {
    return headings;
  }

  // Performance optimization: Create Set for O(1) lookup
  const levelsSet = new Set(includedLevels);

  return headings.filter(heading => levelsSet.has(heading.level));
}

/**
 * Filters out hidden headings
 *
 * Takes a list of headings and removes those whose ID is in the
 * hiddenHeadings array.
 *
 * @param {Array} headings - Array of heading objects
 * @param {Array} hiddenHeadings - Array of heading IDs to hide
 * @returns {Array} Filtered array of heading objects
 *
 * @example
 *   filterHiddenHeadings(headings, ['intro-2', 'deprecated'])
 *   // Returns headings without these IDs
 */
export function filterHiddenHeadings(headings, hiddenHeadings) {
  // Guard against invalid input
  if (!Array.isArray(headings)) {
    return [];
  }

  if (!Array.isArray(hiddenHeadings) || hiddenHeadings.length === 0) {
    return headings;
  }

  // Performance optimization: Create Set for O(1) lookup
  const hiddenSet = new Set(hiddenHeadings);

  return headings.filter(heading => !hiddenSet.has(heading.id));
}

/**
 * Builds a nested hierarchy structure from flat headings
 *
 * Takes a flat array of headings and creates a nested structure based
 * on heading levels. Each heading includes a nesting level for indentation.
 * Handles edge cases like deeply nested structures (H1→H6 jumps).
 *
 * @param {Array} headings - Array of heading objects
 * @returns {Array} Array of heading objects with nestingLevel property
 *
 * @example
 *   buildHierarchy([
 *     { id: "intro", text: "Introduction", level: 2 },
 *     { id: "setup", text: "Setup", level: 3 },
 *     { id: "features", text: "Features", level: 2 }
 *   ])
 *   // Returns: [
 *   //   { id: "intro", text: "Introduction", level: 2, nestingLevel: 0 },
 *   //   { id: "setup", text: "Setup", level: 3, nestingLevel: 1 },
 *   //   { id: "features", text: "Features", level: 2, nestingLevel: 0 }
 *   // ]
 */
export function buildHierarchy(headings) {
  // Guard against invalid input
  if (!Array.isArray(headings) || headings.length === 0) {
    return [];
  }

  // Find the minimum level to use as base (nestingLevel 0)
  // This handles cases where documents don't start with H1
  const minLevel = Math.min(...headings.map(h => h.level));

  // Add nesting level to each heading
  // Use relative nesting based on minimum level in document
  return headings.map(heading => ({
    ...heading,
    nestingLevel: heading.level - minLevel
  }));
}

/**
 * Main extraction function
 *
 * Extracts headings from a document and applies all filters and
 * hierarchy building. This is the primary function to use for TOC.
 * Optimized for performance with large documents.
 *
 * @param {Array} document - BlockNote document (array of blocks)
 * @param {Object} options - Filter options
 * @param {Array} options.includedLevels - Levels to include (default: [2, 3])
 * @param {Array} options.hiddenHeadings - IDs to hide (default: [])
 * @returns {Array} Array of heading objects with hierarchy
 *
 * @example
 *   const headings = extractHeadings(editor.document, {
 *     includedLevels: [2, 3],
 *     hiddenHeadings: ['deprecated-section']
 *   });
 */
export function extractHeadings(document, options = {}) {
  // Performance monitoring for large documents
  const startTime = performance.now();

  const {
    includedLevels = [2, 3],
    hiddenHeadings = []
  } = options;

  // Extract all headings
  let headings = extractAllHeadings(document);

  // Apply level filter
  headings = filterByLevels(headings, includedLevels);

  // Apply hidden filter
  headings = filterHiddenHeadings(headings, hiddenHeadings);

  // Build hierarchy
  headings = buildHierarchy(headings);

  // Log performance warning for large documents
  const duration = performance.now() - startTime;
  if (duration > 50 && headings.length > 100) {
    console.warn(`extractHeadings processed ${headings.length} headings in ${duration.toFixed(2)}ms`);
  }

  return headings;
}
